/**
 * Critically-damped / under-damped springs used for every UI-ish motion
 * (lift, scale pops, snapping). Frame-rate independent, never overshoot to NaN.
 */
export class Spring {
  v = 0;
  constructor(public x = 0, public target = x, public k = 180, public d = 18) {}
  update(dt: number) {
    // semi-implicit Euler, sub-stepped for stiff springs
    const n = Math.max(1, Math.ceil(dt / (1 / 120)));
    const h = dt / n;
    for (let i = 0; i < n; i++) {
      const a = this.k * (this.target - this.x) - this.d * this.v;
      this.v += a * h;
      this.x += this.v * h;
    }
    return this.x;
  }
  snap(x: number) { this.x = this.target = x; this.v = 0; }
  kick(v: number) { this.v += v; }
}

export class Spring3 {
  readonly x: Spring; readonly y: Spring; readonly z: Spring;
  constructor(x = 0, y = 0, z = 0, k = 180, d = 18) {
    this.x = new Spring(x, x, k, d); this.y = new Spring(y, y, k, d); this.z = new Spring(z, z, k, d);
  }
  set(x: number, y: number, z: number) { this.x.target = x; this.y.target = y; this.z.target = z; }
  snap(x: number, y: number, z: number) { this.x.snap(x); this.y.snap(y); this.z.snap(z); }
  tune(k: number, d: number) { for (const s of [this.x, this.y, this.z]) { s.k = k; s.d = d; } }
  update(dt: number) { this.x.update(dt); this.y.update(dt); this.z.update(dt); }
}

export const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
export const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
export const easeInCubic = (t: number) => t * t * t;
export const easeOutBack = (t: number, s = 1.7) => 1 + (s + 1) * (t - 1) ** 3 + s * (t - 1) ** 2;
/** Exponential smoothing factor for a given rate (1/s) — frame-rate independent lerp. */
export const damp = (rate: number, dt: number) => 1 - Math.exp(-rate * dt);
